import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { OnboardingFlow } from "@/components/onboarding/OnboardingFlow";
import { GettingStartedChecklist } from "@/components/help/GettingStartedChecklist";
import { ComplianceMetricsDashboard } from "@/components/dashboard/ComplianceMetricsDashboard";
import { ComplianceWorkloadDashboard } from "@/components/dashboard/ComplianceWorkloadDashboard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getDashboardMetrics, getSportDistribution, getRiskDistribution, getAIMetrics } from "@/services/analyticsService";
import { AIPerformanceBadge } from "@/components/dashboard/AIPerformanceBadge";
import { AIMetricsRow } from "@/components/dashboard/AIMetricsRow";
import { useUserRole } from "@/hooks/useUserRole";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { TrendingUp, Users, CheckCircle, Clock, AlertTriangle, DollarSign } from "lucide-react";

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function Dashboard() {
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { hasAnyRole } = useUserRole();

  // NOTE: This is UX only. Authorization enforced by RLS policies in database
  const canViewAIMetrics = hasAnyRole(['super_admin', 'compliance_officer', 'athletic_director']);

  const { data: metrics } = useQuery({
    queryKey: ["dashboard_metrics"],
    queryFn: getDashboardMetrics,
  });

  const { data: sportDistribution } = useQuery({
    queryKey: ["sport_distribution"],
    queryFn: getSportDistribution,
  });

  const { data: riskDistribution } = useQuery({
    queryKey: ["risk_distribution"],
    queryFn: getRiskDistribution,
  });

  const { data: aiMetrics } = useQuery({
    queryKey: ["ai_metrics"],
    queryFn: getAIMetrics,
    enabled: canViewAIMetrics,
  });

  useEffect(() => {
    const checkOnboarding = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from("profiles" as any)
        .select("*")
        .eq("id", user.id)
        .single();

      const profileData = profile as any;
      if (!profileData?.institution || !profileData?.name) {
        setShowOnboarding(true);
      }
    };

    checkOnboarding();
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  return (
    <>
      <OnboardingFlow 
        open={showOnboarding} 
        onComplete={() => {
          setShowOnboarding(false);
          window.location.reload();
        }} 
      />
      <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground">
            Overview of your NIL compliance system
          </p>
        </div>

        <GettingStartedChecklist />

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="workload">AI Workload</TabsTrigger>
            <TabsTrigger value="metrics">Compliance Metrics</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">

        {canViewAIMetrics && aiMetrics && aiMetrics.totalAIAnalyses > 0 && (
          <AIPerformanceBadge 
            violationsPrevented={aiMetrics.aiViolationsPrevented}
            totalAnalyses={aiMetrics.totalAIAnalyses}
          />
        )}

        {canViewAIMetrics && aiMetrics && aiMetrics.totalAIAnalyses > 0 && (
          <div className="space-y-2">
            <h2 className="text-xl font-semibold">AI-Powered Insights</h2>
            <AIMetricsRow metrics={aiMetrics} />
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Deals</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics?.totalDeals || 0}</div>
              <p className="text-xs text-muted-foreground">
                {formatCurrency(metrics?.totalDealValue || 0)} total value
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Athletes</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics?.activeAthletes || 0}</div>
              <p className="text-xs text-muted-foreground">
                Registered in system
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Compliance Rate</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {metrics?.complianceRate.toFixed(1) || 0}%
              </div>
              <p className="text-xs text-muted-foreground">
                Deals approved
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Reviews</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics?.pendingReviews || 0}</div>
              <p className="text-xs text-muted-foreground">
                Avg {metrics?.avgReviewTime.toFixed(1) || 0} days review time
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>High-Risk Deals Blocked</CardTitle>
                <CardDescription>Critical compliance interventions</CardDescription>
              </div>
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-destructive">
                {metrics?.highRiskBlocked || 0}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Total Deal Value</CardTitle>
                <CardDescription>Combined value of all deals</CardDescription>
              </div>
              <DollarSign className="h-5 w-5 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold">
                {formatCurrency(metrics?.totalDealValue || 0)}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Deals by Sport</CardTitle>
              <CardDescription>Distribution across athletic programs</CardDescription>
            </CardHeader>
            <CardContent>
              {sportDistribution && sportDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={sportDistribution}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="sport" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#3b82f6" name="Deal Count" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Risk Distribution</CardTitle>
              <CardDescription>Compliance risk assessment breakdown</CardDescription>
            </CardHeader>
            <CardContent>
              {riskDistribution && riskDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={riskDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => `${entry.riskLevel}: ${entry.percentage.toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {riskDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  No data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>
          </TabsContent>

          <TabsContent value="workload" className="space-y-6">
            <ComplianceWorkloadDashboard />
          </TabsContent>

          <TabsContent value="metrics">
            <ComplianceMetricsDashboard />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
    </>
  );
}
